import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, FileText, Shield, Activity, TrendingUp, Share2, CheckCircle, ChevronDown, Sparkles, ArrowRight, Mail, X, Facebook, Twitter, Linkedin, Star, Stethoscope, Bell, BookOpen, QrCode, Calendar, Menu } from 'lucide-react';
import AppImage from 'components/AppImage';
import TestimonialsSection from './components/TestimonialsSection';
import { useGoogleAnalytics, trackLandingPageVisit, trackSignupClick, trackSignInClick } from '../../hooks/useGoogleAnalytics';
import { QRCodeSVG } from 'qrcode.react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [faqSectionOpen, setFaqSectionOpen] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showShareQR, setShowShareQR] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroRef = useRef(null);

  // Collapsible section states (collapsed by default)
  const [pricingOpen, setPricingOpen] = useState(false);
  const [testimonialsOpen, setTestimonialsOpen] = useState(false);
  const [prepareOpen, setPrepareOpen] = useState(false);
  const [doctorVisitOpen, setDoctorVisitOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);

  useGoogleAnalytics();

  // Fire landing_page_visit on mount
  useEffect(() => {
    trackLandingPageVisit();
  }, []);

  // Scroll progress + sticky CTA + active section tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement?.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(progress);

      // Show sticky CTA after scrolling past hero (approx 500px)
      const heroHeight = heroRef?.current?.offsetHeight || 500;
      setShowStickyCTA(scrollTop > heroHeight);

      // Active section tracking for breadcrumb
      const sections = ['hero', 'problem', 'features', 'prepare', 'summary', 'pricing', 'cta'];
      for (const id of [...sections]?.reverse()) {
        const el = id === 'hero' ? heroRef?.current : document.getElementById(id);
        if (el && scrollTop >= el?.offsetTop - 120) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const shareUrl = 'https://mybodytoda1849.builtwithrocket.new';
  const shareTitle = 'MyBodyToday – Track your health symptoms daily';
  const shareText = 'Check out MyBodyToday – a simple daily symptom journal that helps you track health changes and create doctor-ready summaries.';

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
      } catch (err) {
        if (err?.name !== 'AbortError') setShowShareMenu(true);
      }
    } else {
      setShowShareMenu(prev => !prev);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body?.appendChild(el);
      el?.select();
      document.execCommand('copy');
      document.body?.removeChild(el);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const shareOptions = [
    {
      label: 'Facebook',
      icon: Facebook,
      color: 'text-blue-600',
      bg: 'hover:bg-blue-50',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    },
    {
      label: 'Twitter / X',
      icon: Twitter,
      color: 'text-sky-500',
      bg: 'hover:bg-sky-50',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
    },
    {
      label: 'LinkedIn',
      icon: Linkedin,
      color: 'text-blue-700',
      bg: 'hover:bg-blue-50',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      label: 'Email',
      icon: Mail,
      color: 'text-green-600',
      bg: 'hover:bg-green-50',
      href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const coreFeatures = [
    { icon: Activity, title: 'Daily symptom tracking', description: 'Log how you feel each day in under a minute' },
    { icon: TrendingUp, title: 'Symptom timeline', description: 'See your health changes over time at a glance' },
    { icon: FileText, title: 'Consultation-ready report', description: 'Generate a clear summary before your appointment' },
    { icon: Bell, title: 'Trigger tracking', description: 'Identify what makes symptoms better or worse' },
    { icon: BookOpen, title: 'Patient notes', description: 'Add personal notes and observations to each entry' },
    { icon: Calendar, title: 'Appointment reminders', description: 'Manage up to 3 doctor appointments with 3-tier email reminders' },
  ];

  const faqs = [
    {
      id: 1,
      category: 'Getting Started',
      question: 'What is MyBodyToday?',
      answer: 'MyBodyToday is a daily symptom journal that helps you record how you feel, track changes over time, and generate clear summaries you can show during doctor appointments. It\'s designed for individuals, parents, carers, and people living with chronic conditions.'
    },
    {
      id: 2,
      category: 'Getting Started',
      question: 'Is MyBodyToday free to use?',
      answer: 'Yes! The Free Plan is completely free forever and includes unlimited symptom tracking and symptom timeline view — no credit card required. Upgrade to Premium (£4.99/month) to unlock doctor consultation summaries, patient notes, trigger tracking, and doctor appointment reminders.'
    },
    {
      id: 3,
      category: 'Getting Started',
      question: 'How do I get started?',
      answer: 'Simply sign up with your email, complete your profile, and start your first daily check-in. Our interactive onboarding wizard will guide you through the process. Email verification is required to access all features.'
    },
    {
      id: 4,
      category: 'Daily Check-In',
      question: 'How often should I log symptoms?',
      answer: 'We recommend daily check-ins for the most accurate tracking. However, you can log symptoms as frequently as needed. The more consistent your tracking, the better insights you\'ll gain.'
    },
    {
      id: 5,
      category: 'Daily Check-In',
      question: 'Can I add photos or voice notes?',
      answer: 'Yes! You can attach photos (like rashes or swelling) and record voice notes to provide additional context for your symptoms. This helps create a more complete health record. All attachments are automatically encrypted for your privacy.'
    },
    {
      id: 6,
      category: 'Daily Check-In',
      question: 'What if I forget to log a day?',
      answer: 'No problem! You can backdate entries to fill in missed days. Just select the date you want to log for in the calendar view.'
    },
    {
      id: 7,
      category: 'Daily Check-In',
      question: 'Can I track symptoms for multiple people?',
      answer: 'Yes! Premium plan users can manage caregiver accounts to track symptoms for dependents such as children or elderly family members. Add dependents through User Relationship Management.'
    },
    {
      id: 8,
      category: 'Data & Export',
      question: 'How do I share data with my doctor?',
      answer: 'Go to Doctor Summary Export, select your date range, and generate a professional report. You can email it directly to your doctor or download it for your appointment. The Free Plan includes basic report export.'
    },
    {
      id: 9,
      category: 'Data & Export',
      question: 'What formats can I export data in?',
      answer: 'You can export your symptom data as PDF (formatted reports) or CSV (spreadsheet format for analysis). The Free Plan includes basic report export. Premium users get unlimited PDF doctor reports.'
    },
    {
      id: 10,
      category: 'Data & Export',
      question: 'Can I export all my data at once?',
      answer: 'Yes! Go to Settings & Privacy > Data Management and select "Export All Data" to download your complete health record. This includes all symptom entries, medications, notes, and attachments.'
    },
    {
      id: 11,
      category: 'Subscription',
      question: 'What\'s included in the Free Plan?',
      answer: 'The Free Plan is completely free forever and includes unlimited symptom tracking and symptom timeline view. No credit card required, no time limits.'
    },
    {
      id: 12,
      category: 'Subscription',
      question: 'What\'s included in the Premium Plan?',
      answer: 'Premium (£4.99/month) includes everything in the Free Plan plus: doctor consultation summary, patient notes, trigger tracking, and doctor appointment reminders (save up to 3 upcoming appointments with automatic 3-tier email reminders at 7 days, 1 day, and 2 hours before each appointment).'
    },
    {
      id: 13,
      category: 'Subscription',
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes! You can cancel anytime from Subscription Management. You\'ll retain access until the end of your billing period, and your data is never deleted. You can reactivate your subscription at any time.'
    },
    {
      id: 14,
      category: 'Subscription',
      question: 'Do you offer annual billing?',
      answer: 'Yes! Annual plans offer savings compared to monthly billing. Annual subscribers also get priority support and early access to new features.'
    },
    {
      id: 15,
      category: 'Doctor Appointments',
      question: 'Can I manage my doctor appointments in MyBodyToday?',
      answer: 'Yes! Premium users can save up to 3 upcoming doctor appointments and receive automatic email reminders 7 days, 1 day, and 2 hours before each appointment. Each reminder includes a direct link to generate your consultation summary so you arrive fully prepared.'
    },
    {
      id: 16,
      category: 'Doctor Appointments',
      question: 'How do I unlock the appointment reminders feature?',
      answer: 'Appointment reminders are available on the Premium plan at £4.99/month. Alternatively, refer 10 friends to MyBodyToday and unlock all Premium features permanently for free.'
    },
    {
      id: 17,
      category: 'Caregiver Features',
      question: 'How do I add dependents as a caregiver?',
      answer: 'Go to User Relationship Management from the bottom navigation menu and click "Add Dependent". Fill out their information including name, relationship type, and optional email. Caregiver account management is a Premium feature.'
    },
    {
      id: 18,
      category: 'Caregiver Features',
      question: 'Can dependents see my data?',
      answer: 'No. Caregiver relationships are one-way. You can view and manage their symptom data, but they cannot see yours unless you explicitly share it. All data is encrypted separately for each profile.'
    },
    {
      id: 19,
      category: 'Referral Program',
      question: 'How does the referral program work?',
      answer: 'Share your unique referral link with friends. Refer 5 friends to unlock the Doctor Consultation Summary report for free. Refer 10 friends to unlock the full Premium plan permanently for free — including doctor consultation summary, patient notes, trigger tracking, and appointment reminders. You can also upgrade directly for £4.99/month.'
    },
    {
      id: 20,
      category: 'Privacy & Security',
      question: 'Is my health data secure?',
      answer: 'Yes. We use mandatory military-grade encryption (AES-256) for all sensitive health data. Your symptom entries, medications, notes, photos, and voice recordings are automatically encrypted on your device before being stored.'
    },
    {
      id: 21,
      category: 'Privacy & Security',
      question: 'How do I delete my account?',
      answer: 'Go to Settings & Privacy > Data Management > Delete Account. This permanently removes all your data within 30 days. You can export your data first if needed.'
    },
    {
      id: 22,
      category: 'Analytics',
      question: 'What are AI pattern insights?',
      answer: 'Our AI analyses your symptom history to identify recurring patterns, correlations between symptoms, and potential triggers. This helps you understand what affects your health. Available for Premium plan subscribers.'
    },
    {
      id: 23,
      category: 'Technical',
      question: 'Which devices are supported?',
      answer: 'MyBodyToday works on any device with a modern web browser (Chrome, Safari, Firefox, Edge). We\'re mobile-optimised and working on native iOS and Android apps.'
    },
    {
      id: 24,
      category: 'Technical',
      question: 'Why do I need to verify my email?',
      answer: 'Email verification is required to access all features of MyBodyToday. This ensures account security, enables password recovery, and allows us to send important notifications. Check your inbox and spam folder for the verification link.'
    }
  ];

  // Section breadcrumb config
  const breadcrumbSections = [
    { id: 'hero', label: 'Hero' },
    { id: 'problem', label: 'How it Works' },
    { id: 'features', label: 'See the Report' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'cta', label: 'Get Started' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-muted" onClick={() => { showShareMenu && setShowShareMenu(false); mobileMenuOpen && setMobileMenuOpen(false); }}>
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[100] h-1 bg-gray-200">
        <div
          className="h-full bg-primary transition-all duration-100 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      {/* Navigation */}
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-1 z-50 bg-white/80 backdrop-blur-lg border-b border-border shadow-therapeutic-sm">
        
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16">
          <div className="flex justify-between items-center h-16 sm:h-18 sm:py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <AppImage
                src="/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png"
                alt="MyBodyToday logo - health tracking application"
                className="h-16 w-16 sm:h-20 sm:w-20 object-contain" />
              
              <div className="flex flex-col leading-tight">
                <span className="text-lg sm:text-2xl font-heading font-extrabold text-foreground tracking-tight">
                  MyBodyToday&#8482;
                </span>
                <span className="text-xs text-muted-foreground italic hidden sm:block" style={{ fontSize: '0.65rem' }}>
                  track small changes before they become big ones
                </span>
              </div>
            </div>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-8 text-sm font-medium">
              <a href="#problem" className="text-muted-foreground hover:text-primary transition-colors">Why it matters</a>
              <a href="#features" onClick={() => setFeaturesOpen(true)} className="text-muted-foreground hover:text-primary transition-colors">Features</a>
              <a href="#doctor-visit" onClick={() => setDoctorVisitOpen(true)} className="text-muted-foreground hover:text-primary transition-colors">Doctor visit</a>
              <a href="#pricing" onClick={() => setPricingOpen(true)} className="text-muted-foreground hover:text-primary transition-colors">Pricing</a>
              <a href="#faq" className="text-muted-foreground hover:text-primary transition-colors">FAQ</a>
            </div>

            {/* Right side: Sign In + Share (desktop) + Hamburger (mobile) */}
            <div className="flex items-center gap-2 sm:gap-3 relative" onClick={e => e?.stopPropagation()}>
              {/* Sign In — visible on all sizes */}
              <Link
                to="/sign-in"
                onClick={() => trackSignInClick('navbar')}
                className="flex items-center gap-1.5 px-3 py-2 sm:px-5 sm:py-2.5 border border-primary text-primary rounded-lg font-semibold text-sm transition-all duration-200 hover:bg-primary/10 whitespace-nowrap">
                Sign In
              </Link>

              {/* Share — hidden on mobile (xs), visible sm+ */}
              <button
                onClick={handleShare}
                className="hidden sm:flex items-center gap-2 px-4 py-2.5 sm:px-7 sm:py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 hover:bg-primary/90 hover:scale-105 hover:shadow-lg shadow-therapeutic">
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Share</span>
              </button>

              {/* Hamburger — visible on mobile only */}
              <button
                onClick={() => setMobileMenuOpen(prev => !prev)}
                aria-label="Open menu"
                className="sm:hidden flex items-center justify-center w-9 h-9 rounded-lg border border-border text-foreground hover:bg-muted/50 transition-colors">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>

              {/* Desktop share dropdown */}
              <AnimatePresence>
                {showShareMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-therapeutic-xl border border-border z-50 overflow-hidden">
                    
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Share MyBodyToday</p>
                    </div>

                    {shareOptions?.map((opt) => (
                      <a
                        key={opt?.label}
                        href={opt?.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground ${opt?.bg} transition-colors`}
                        onClick={() => setShowShareMenu(false)}>
                        <opt.icon className={`w-4 h-4 ${opt?.color}`} />
                        {opt?.label}
                      </a>
                    ))}

                    {/* QR Code option replacing Copy link */}
                    <button
                      onClick={() => setShowShareQR(prev => !prev)}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors border-t border-border">
                      <QrCode className="w-4 h-4 text-muted-foreground" />
                      {showShareQR ? 'Hide QR Code' : 'Show QR Code'}
                    </button>

                    {showShareQR && (
                      <div className="px-4 pb-4 flex flex-col items-center gap-2 border-t border-border bg-gray-50">
                        <p className="text-xs text-muted-foreground pt-3 text-center">Scan to open MyBodyToday</p>
                        <div className="bg-white p-2 rounded-lg border border-border shadow-sm">
                          <QRCodeSVG
                            value={shareUrl}
                            size={140}
                            level="M"
                            includeMargin={false}
                            fgColor="#166534"
                          />
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile dropdown menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="sm:hidden overflow-hidden border-t border-border"
              >
                <nav className="flex flex-col py-3 gap-1">
                  <a href="#problem" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted/50 rounded-lg transition-colors">Why it matters</a>
                  <a href="#features" onClick={() => { setFeaturesOpen(true); setMobileMenuOpen(false); }} className="px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted/50 rounded-lg transition-colors">Features</a>
                  <a href="#doctor-visit" onClick={() => { setDoctorVisitOpen(true); setMobileMenuOpen(false); }} className="px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted/50 rounded-lg transition-colors">Doctor visit</a>
                  <a href="#pricing" onClick={() => { setPricingOpen(true); setMobileMenuOpen(false); }} className="px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted/50 rounded-lg transition-colors">Pricing</a>
                  <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted/50 rounded-lg transition-colors">FAQ</a>
                  <div className="border-t border-border mt-1 pt-2 px-4 pb-1">
                    <button
                      onClick={() => { handleShare(); setMobileMenuOpen(false); }}
                      className="flex items-center gap-2 w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold text-sm transition-all hover:bg-primary/90">
                      <Share2 className="w-4 h-4" />
                      Share MyBodyToday
                    </button>
                  </div>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>
      {/* Hero Section */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        
        <div className="relative w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16 py-10 sm:py-16 lg:py-20">
          {/* Full-width headline row */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full mb-10">
            
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Your health companion</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-foreground leading-tight mb-4">
              Track small changes<br />
              before they become{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-secondary">
big ones
              </span><sup className="text-sm sm:text-base lg:text-lg align-super">™</sup>
            </h1>
          </motion.div>

          {/* Content + Card row */}
          <div className="grid lg:grid-cols-2 gap-12 xl:gap-20 items-start w-full">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}>
              
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8">
                MyBodyToday helps you track symptoms daily and generate clear summaries for doctor consultations.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-3">
                <Link
                  to="/sign-up"
                  onClick={() => trackSignupClick('hero_start_free')}
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-bold text-base transition-all duration-200 hover:bg-primary/90 hover:scale-105 hover:shadow-xl shadow-therapeutic-lg">
                  
                  Start Tracking for Free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* Reassurance message */}
              <p className="text-sm text-muted-foreground mb-6">No credit card required.</p>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span>No diagnosis. No medical advice. Just clear tracking and better conversations.</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative">
              
              <div className="bg-white rounded-2xl shadow-therapeutic-xl border border-border p-8">
                <p className="text-muted-foreground mb-6">
                  Want to help shape it? Share your feedback after your first check-in!
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Health for the Whole Family - moved here, directly below hero */}
      <section id="demo" className="py-4 sm:py-6 bg-gradient-to-br from-card via-muted to-background">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-8">
            
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full mb-4">
              <Heart className="w-4 h-4 text-accent" />
              <span className="text-sm font-semibold text-accent">Health for the whole family</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-heading font-bold text-foreground mb-3">
              Track health for everyone you care about
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              From grandparents to grandchildren, MyBodyToday helps families stay healthy together
            </p>
          </motion.div>

          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative group">
            
            <div className="relative bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 rounded-2xl shadow-therapeutic-xl border border-border overflow-hidden transition-all duration-300 group-hover:shadow-xl group-hover:border-primary/20">
              <div className="aspect-video relative">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_17bd9148e-1765771979332.png"
                  alt="Happy, healthy mixed race family - Grand Dad and Mum, Father and Mother, and two Children all smiling together, feeling grateful for life and good health"
                  className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/20 rounded-full blur-2xl -z-10" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-accent/20 rounded-full blur-2xl -z-10" />
          </motion.div>
        </div>
      </section>
      {/* How It Works / Problem Section */}
      <section id="problem" className="py-6 sm:py-8 bg-white">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center">
            
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-6">
              <Stethoscope className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">Sound familiar?</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
              "I forgot to mention that symptom at my last appointment."
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              Many people leave doctor appointments and later realise they forgot to mention important symptoms. MyBodyToday helps you capture changes as they happen so you can communicate clearly during consultations.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Features Section */}
      <section id="features" className="py-6 sm:py-8 bg-gradient-to-br from-card via-muted to-background">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-xl overflow-hidden shadow-therapeutic hover:shadow-therapeutic-lg transition-shadow">

            <button
              onClick={() => setFeaturesOpen(!featuresOpen)}
              className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <span className="text-base sm:text-lg font-bold text-foreground block">Everything you need</span>
                  <span className="text-sm text-muted-foreground">Five core tools to help you track, understand, and communicate your health</span>
                </div>
              </div>
              <ChevronDown
                className={`w-6 h-6 text-primary flex-shrink-0 transition-transform ${featuresOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {featuresOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-border">
                  <div className="p-6 sm:p-10">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                      {coreFeatures?.map((feature, index) =>
                        <motion.div
                          key={index}
                          initial={{ y: 50, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.1 * index }}
                          className="relative group">
                          
                          <div className="relative bg-white border border-border rounded-2xl p-6 shadow-therapeutic transition-all duration-300 hover:shadow-therapeutic-lg hover:-translate-y-2 hover:border-primary/30 h-full cursor-pointer">
                            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4 mx-auto">
                              <feature.icon className="w-6 h-6 text-primary" />
                            </div>
                            <h3 className="text-sm sm:text-base font-heading font-bold text-foreground mb-2 text-center">{feature?.title}</h3>
                            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed text-center">{feature?.description}</p>
                          </div>
                        </motion.div>
                      )}
                    </div>

                    <div className="text-center">
                      <Link
                        to="/sign-up"
                        onClick={() => trackSignupClick('features_get_started')}
                        className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-bold text-base transition-all duration-200 hover:bg-primary/90 hover:scale-105 hover:shadow-xl shadow-therapeutic-lg">
                        Start Tracking for Free
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                      <p className="text-sm text-muted-foreground mt-3">Free to use. No credit card required.</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      {/* Doctor Visit Section */}
      <section id="doctor-visit" className="py-2 sm:py-3 bg-white">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-xl overflow-hidden shadow-therapeutic hover:shadow-therapeutic-lg transition-shadow">

            <button
              onClick={() => setDoctorVisitOpen(!doctorVisitOpen)}
              className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Stethoscope className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <span className="text-base sm:text-lg font-bold text-foreground block">See what your doctor will see</span>
                  <span className="text-sm text-muted-foreground">A clear consultation summary you can generate before your appointment</span>
                </div>
              </div>
              <ChevronDown
                className={`w-6 h-6 text-primary flex-shrink-0 transition-transform ${doctorVisitOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {doctorVisitOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-border">
                  <div className="p-6 max-w-3xl mx-auto">
                    {/* Doctor-ready badge */}
                    <div className="flex justify-center mb-6">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full">
                        <Stethoscope className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-700">Doctor-ready in seconds</span>
                      </div>
                    </div>

                    {/* Heading + subtitle */}
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-foreground text-center mb-3">
                      See what your doctor will see
                    </h2>
                    <p className="text-base text-muted-foreground text-center mb-8">
                      A clear consultation summary you can generate before your appointment.
                    </p>

                    {/* Patient Consultation Summary Card */}
                    <div className="rounded-2xl overflow-hidden border border-green-200 shadow-therapeutic-lg mb-8">
                      {/* Green header */}
                      <div className="bg-green-600 px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">MyBodyToday™</p>
                            <p className="text-xs text-green-100">Patient Consultation Summary</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-white">Report Period</p>
                          <p className="text-xs text-green-100">1 Feb – 28 Feb 2025</p>
                        </div>
                      </div>

                      {/* Card body */}
                      <div className="bg-white p-5 space-y-4">

                        {/* Row 1: Primary Concern + Severity + Trend */}
                        <div className="grid sm:grid-cols-3 gap-3">
                          {/* Primary Concern */}
                          <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                            <p className="text-xs font-bold text-red-500 uppercase tracking-wide mb-2">Primary Concern</p>
                            <p className="text-base font-bold text-foreground">Persistent Headache</p>
                            <p className="text-sm text-muted-foreground">Location: Frontal / Temporal</p>
                          </div>

                          {/* Severity */}
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <p className="text-xs text-muted-foreground mb-1">Severity</p>
                            <p className="text-base font-bold text-red-500">7/10 — High</p>
                          </div>

                          {/* Trend */}
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <p className="text-xs text-muted-foreground mb-1">Trend</p>
                            <p className="text-base font-bold text-red-500 flex items-center gap-1">
                              <TrendingUp className="w-4 h-4" /> Increasing
                            </p>
                          </div>
                        </div>

                        {/* Frequency */}
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <p className="text-xs text-muted-foreground mb-1">Frequency</p>
                          <p className="text-sm font-semibold text-foreground">Daily for 14 days</p>
                        </div>

                        {/* Next Appointment */}
                        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                          <p className="text-xs text-muted-foreground mb-1">Next Appointment</p>
                          <p className="text-sm font-semibold text-green-700 flex items-center gap-2">
                            <span>Dr. Patel — In 6 days · Reminder set</span>
                            <span className="text-green-500">✓</span>
                          </p>
                        </div>

                        {/* Row: Possible Triggers + Impact on Daily Life */}
                        <div className="grid sm:grid-cols-2 gap-3">
                          {/* Possible Triggers */}
                          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                            <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-3">Possible Triggers</p>
                            <div className="flex flex-wrap gap-2">
                              <span className="px-3 py-1 bg-white border border-amber-200 rounded-full text-xs font-medium text-amber-700">Stress</span>
                              <span className="px-3 py-1 bg-white border border-amber-200 rounded-full text-xs font-medium text-amber-700">Poor sleep</span>
                              <span className="px-3 py-1 bg-white border border-amber-200 rounded-full text-xs font-medium text-amber-700">Screen time</span>
                            </div>
                          </div>

                          {/* Impact on Daily Life */}
                          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                            <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-3">Impact on Daily Life</p>
                            <ul className="space-y-1">
                              <li className="text-xs text-blue-700 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0"></span>
                                Sleep disrupted
                              </li>
                              <li className="text-xs text-blue-700 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0"></span>
                                Reduced concentration at work
                              </li>
                            </ul>
                          </div>
                        </div>

                        {/* Symptom Timeline */}
                        <div className="rounded-xl p-4 border border-gray-100 bg-gray-50">
                          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-4 flex items-center gap-2">
                            <span>📅</span> Symptom Timeline
                          </p>
                          <div className="space-y-3">
                            {[
                              { date: '28 Feb', desc: 'Woke with throbbing pain, lasted 4 hours', severity: 7, color: 'bg-red-400' },
                              { date: '26 Feb', desc: 'Afternoon onset, screen time likely trigger', severity: 6, color: 'bg-orange-400' },
                              { date: '24 Feb', desc: 'Worst episode — nausea alongside headache', severity: 8, color: 'bg-red-500' },
                              { date: '22 Feb', desc: 'Mild, resolved after rest', severity: 5, color: 'bg-yellow-400' },
                              { date: '20 Feb', desc: 'Morning headache, stress at work', severity: 6, color: 'bg-orange-400' },
                            ]?.map((entry, i) => (
                              <div key={i} className="flex items-start gap-3">
                                <span className="text-xs font-semibold text-muted-foreground w-12 flex-shrink-0 pt-0.5">{entry?.date}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-foreground mb-1">{entry?.desc}</p>
                                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div
                                      className={`${entry?.color} h-1.5 rounded-full`}
                                      style={{ width: `${(entry?.severity / 10) * 100}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <span className="text-xs font-bold text-muted-foreground flex-shrink-0 pt-0.5">{entry?.severity}/10</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Patient Notes */}
                        <div className="rounded-xl p-4 border border-gray-100 bg-white">
                          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-2">
                            <span>ℹ️</span> Patient Notes
                          </p>
                          <p className="text-xs text-muted-foreground italic leading-relaxed">
                            "Headaches are <span className="text-red-500 not-italic font-medium">worst in the morning</span> and <span className="text-red-500 not-italic font-medium">after long screen sessions</span>. Paracetamol provides temporary relief but symptoms return. I'm concerned about the <span className="text-red-500 not-italic font-medium">increasing frequency over the past two weeks</span>."
                          </p>
                        </div>

                        {/* Disclaimer */}
                        <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                          <span>⚠️</span> Patient-generated symptom summary. Not a medical diagnosis. For clinical use only.
                        </p>
                      </div>
                    </div>

                    {/* CTA below card */}
                    <div className="text-center">
                      <p className="text-base sm:text-lg font-semibold text-foreground mb-4">
                        Generate this report in <span className="font-bold">30 seconds</span> before your doctor visit.
                      </p>
                      <Link
                        to="/sign-up"
                        onClick={() => trackSignupClick('doctor_visit_section')}
                        className="inline-flex items-center gap-2 px-8 py-4 bg-green-700 text-white rounded-xl font-bold text-base transition-all duration-200 hover:bg-green-800 hover:scale-105 hover:shadow-xl shadow-therapeutic-lg">
                        Try it free →
                      </Link>
                      <p className="text-xs text-muted-foreground mt-3">No credit card required.</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      {/* Trusted by patients and clinicians — collapsible Testimonials */}
      <section className="py-2 sm:py-3 bg-gradient-to-br from-card via-muted to-background">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-xl overflow-hidden shadow-therapeutic hover:shadow-therapeutic-lg transition-shadow">
            
            <button
              onClick={() => setTestimonialsOpen(!testimonialsOpen)}
              className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Star className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <span className="text-base sm:text-lg font-bold text-foreground block">Trusted by patients and clinicians</span>
                  <span className="text-sm text-muted-foreground">See what people are saying about MyBodyToday</span>
                </div>
              </div>
              <ChevronDown
                className={`w-6 h-6 text-primary flex-shrink-0 transition-transform ${testimonialsOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {testimonialsOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-border">
                  <div className="p-0">
                    <TestimonialsSection embedded />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      {/* Simple, honest pricing — collapsible */}
      <section id="pricing" className="py-2 sm:py-3 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-xl overflow-hidden shadow-therapeutic hover:shadow-therapeutic-lg transition-shadow">
            
            <button
              onClick={() => setPricingOpen(!pricingOpen)}
              className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Star className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <span className="text-base sm:text-lg font-bold text-foreground block">Simple, honest pricing</span>
                  <span className="text-sm text-muted-foreground">Start free. Upgrade when you're ready. No hidden fees.</span>
                </div>
              </div>
              <ChevronDown
                className={`w-6 h-6 text-primary flex-shrink-0 transition-transform ${pricingOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {pricingOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-border">
                  <div className="p-6 sm:p-10">
                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                      {/* Free Plan */}
                      <div className="relative">
                        <div className="bg-white rounded-2xl border-2 border-primary shadow-therapeutic-xl p-8 h-full flex flex-col">
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                            <div className="bg-primary text-primary-foreground text-sm font-bold px-5 py-1.5 rounded-full shadow-lg">
                              ⭐ Recommended — Start here
                            </div>
                          </div>
                          <div className="mt-4">
                            <h3 className="text-2xl font-heading font-bold text-foreground mb-1">Free Plan</h3>
                            <p className="text-muted-foreground text-sm mb-4">Everything you need to track your symptoms</p>
                            <div className="text-4xl font-bold text-foreground mb-1">Free</div>
                            <p className="text-sm text-muted-foreground mb-6">Forever. No credit card required.</p>
                            <Link
                              to="/sign-up"
                              onClick={() => trackSignupClick('pricing_free_plan')}
                              className="block w-full text-center py-3 px-6 bg-primary text-primary-foreground rounded-xl font-bold text-base transition-all duration-200 hover:bg-primary/90 hover:scale-105 shadow-therapeutic mb-6">
                              Start Tracking for Free
                            </Link>
                            <div className="space-y-3">
                              {[
                                'Unlimited symptom tracking',
                                'Symptom timeline view',
                              ]?.map((feature, i) => (
                                <div key={i} className="flex items-center gap-3">
                                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                                  <span className="text-sm text-foreground">{feature}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Premium Plan */}
                      <div className="relative">
                        <div className="bg-white rounded-2xl border border-border shadow-therapeutic p-8 h-full flex flex-col">
                          <h3 className="text-2xl font-heading font-bold text-foreground mb-1">Premium</h3>
                          <p className="text-muted-foreground text-sm mb-4">For deeper insights and family tracking</p>
                          <div className="flex items-baseline gap-1 mb-1">
                            <span className="text-4xl font-bold text-foreground">£4.99</span>
                            <span className="text-muted-foreground text-sm">/month</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-6">Cancel anytime</p>
                          <Link
                            to="/subscription-management"
                            className="block w-full text-center py-3 px-6 bg-foreground text-white rounded-xl font-bold text-base transition-all duration-200 hover:bg-foreground/90 hover:scale-105 mb-6">
                            Upgrade to Premium
                          </Link>
                          <div className="space-y-3">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">All Free features, plus:</p>
                            {[
                              'Doctor consultation summary',
                              'Patient notes',
                              'Trigger tracking',
                            ]?.map((feature, i) => (
                              <div key={i} className="flex items-center gap-3">
                                <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                                <span className="text-sm text-foreground">{feature}</span>
                              </div>
                            ))}
                            <div className="mt-4 pt-4 border-t border-border">
                              <p className="text-xs text-muted-foreground italic">Doctor appointment reminders included. Advanced analytics and AI insights coming soon.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-center text-sm text-muted-foreground mt-8">
                      Not sure? Start with the Free Plan — no commitment, no credit card.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      {/* FAQ */}
      <section id="faq" className="py-6 sm:py-8 bg-gradient-to-br from-card via-muted to-background">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-xl overflow-hidden shadow-therapeutic hover:shadow-therapeutic-lg transition-shadow max-w-4xl mx-auto">
            
            <button
              onClick={() => setFaqSectionOpen(!faqSectionOpen)}
              className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
              
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText className="w-7 h-7 text-primary" />
                </div>
                <span className="text-base sm:text-lg font-bold text-foreground">View all FAQs ({faqs?.length} questions)</span>
              </div>
              <ChevronDown
                className={`w-6 h-6 text-primary flex-shrink-0 transition-transform ${faqSectionOpen ? 'rotate-180' : ''}`} />
            </button>

            {faqSectionOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-border">
                
                <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
                  {faqs?.map((faq, index) => (
                    <div
                      key={index}
                      className="bg-white border border-border rounded-lg overflow-hidden shadow-sm transition-all duration-200 hover:shadow-md hover:border-primary/20">
                      
                      <button
                        onClick={() => toggleFaq(index)}
                        className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-muted/30 transition-colors">
                        
                        <div className="flex-1">
                          <span className="text-xs font-semibold text-primary uppercase tracking-wide mb-1 block">
                            {faq?.category}
                          </span>
                          <span className="text-sm font-bold text-foreground">{faq?.question}</span>
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 text-primary flex-shrink-0 ml-3 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                      </button>
                      {openFaq === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="px-4 pb-4">
                          
                          <p className="text-sm text-muted-foreground leading-relaxed">{faq?.answer}</p>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-6 sm:py-8 bg-gradient-to-br from-primary via-accent to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20" />
        
        <div className="relative w-full max-w-screen-2xl mx-auto px-4 sm:px-10 lg:px-16 text-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-heading font-bold text-white mb-4">
              Ready to start tracking?
            </h2>
            <p className="text-base sm:text-lg text-white/90 mb-2 max-w-2xl mx-auto">
              Join thousands of people who track their health with MyBodyToday.
            </p>
            <p className="text-sm text-white/75 mb-6">Free to use. No credit card required.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/sign-up"
                onClick={() => { trackSignupClick('sticky_cta'); }}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-bold text-sm hover:bg-primary/90 hover:scale-105 transition-all hover:shadow-lg">
                
                Start Tracking Free — No credit card required
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-foreground text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <AppImage
                src="/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png"
                alt="MyBodyToday logo footer"
                className="h-8 w-8" />
              
              <span className="text-base sm:text-lg font-heading font-bold">MyBodyToday&#8482;</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/privacy-policy" className="hover:text-primary transition-colors duration-200">
                Privacy
              </Link>
              <Link to="/terms-and-conditions" className="hover:text-primary transition-colors duration-200">
                Terms
              </Link>
              <Link to="/help-and-support" className="hover:text-primary transition-colors duration-200">
                Contact
              </Link>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-sm text-white/60">© 2025 MyBodyToday&#8482;. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;